import { useState } from "react";
import { Icon, Avatar, Modal, Field, Inp, Sel, Txt, SaveBtn } from "../shared/ui.jsx";
import { USER_COLORS, ACT_LOCATIONS, WORK_SUBS, EXERCISE_SUBS } from "../shared/constants.js";
import { displayDate } from "../shared/utils";

export function ActivityForm({ date, user, onSave, onClose }) {
  const [form, setForm] = useState({
    type: "activity", subtype: "", title: "", location: "Home",
    duration: "", intensity: "", notes: "", tags: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = () => {
    if (!form.title.trim()) return;
    onSave({ ...form, id: Date.now(), date, user, createdAt: new Date().toISOString() });
    onClose();
  };
  return (
    <>
      <Field label="Type">
        <Sel value={form.type} onChange={e => { set("type", e.target.value); set("subtype", ""); }}>
          <option value="activity">Activity / Work</option>
          <option value="exercise">Exercise</option>
        </Sel>
      </Field>
      {form.type === "activity" && (
        <Field label="Category">
          <Sel value={form.subtype} onChange={e => set("subtype", e.target.value)}>
            <option value="">— select —</option>
            {WORK_SUBS.map(s => <option key={s}>{s}</option>)}
          </Sel>
        </Field>
      )}
      {form.type === "exercise" && (
        <Field label="Exercise type">
          <Sel value={form.subtype} onChange={e => set("subtype", e.target.value)}>
            <option value="">— select —</option>
            {EXERCISE_SUBS.map(s => <option key={s}>{s}</option>)}
          </Sel>
        </Field>
      )}
      <Field label="Title / Description">
        <Inp value={form.title} onChange={e => set("title", e.target.value)} placeholder="Chopped wood, Yoga session…" />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Duration (min)"><Inp type="number" value={form.duration} onChange={e => set("duration", e.target.value)} placeholder="30" /></Field>
        <Field label="Intensity (1–5)">
          <Sel value={form.intensity} onChange={e => set("intensity", e.target.value)}>
            <option value="">—</option>
            {[1, 2, 3, 4, 5].map(n => <option key={n}>{n}</option>)}
          </Sel>
        </Field>
      </div>
      <Field label="Location">
        <Sel value={form.location} onChange={e => set("location", e.target.value)}>
          {ACT_LOCATIONS.map(l => <option key={l}>{l}</option>)}
        </Sel>
      </Field>
      <Field label="Tags (comma separated)">
        <Inp value={form.tags} onChange={e => set("tags", e.target.value)} placeholder="cardio, nature, rehab…" />
      </Field>
      <Field label="Notes"><Txt value={form.notes} onChange={e => set("notes", e.target.value)} /></Field>
      <SaveBtn onClick={save} />
    </>
  );
}

function ActivityCard({ entry, currentUser, onDelete }) {
  const isOwn = entry.user === currentUser;
  const typeColor = entry.type === "exercise" ? "#5b8a6b" : "#7a6b4e";
  const userColor = USER_COLORS[entry.user] || "var(--accent)";
  return (
    <div style={{ padding: "0.85rem 1rem", borderLeft: `3px solid ${isOwn ? typeColor : userColor}`, background: "var(--paper-dark)", borderRadius: "0 4px 4px 0", marginBottom: "0.6rem", opacity: isOwn ? 1 : 0.85 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: 3, flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.1em", color: typeColor, fontWeight: 600 }}>
              {entry.type}{entry.subtype ? ` · ${entry.subtype}` : ""}
            </span>
            {!isOwn && (
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Avatar user={entry.user} size={15} />
                <span style={{ fontSize: "0.65rem", color: userColor, fontWeight: 600 }}>{entry.user}</span>
              </span>
            )}
          </div>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "1rem", color: "var(--ink)" }}>{entry.title}</div>
          <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginTop: 3 }}>
            {[entry.location, entry.duration && `${entry.duration} min`, entry.intensity && `Intensity ${entry.intensity}`].filter(Boolean).join(" · ")}
          </div>
          {entry.notes && <div style={{ fontSize: "0.82rem", color: "var(--ink70)", marginTop: 5, fontStyle: "italic" }}>{entry.notes}</div>}
          {entry.tags && (
            <div style={{ marginTop: 5 }}>
              {entry.tags.split(",").map(t => t.trim()).filter(Boolean).map(t => (
                <span key={t} style={{ fontSize: "0.68rem", background: "var(--ink10)", color: "var(--ink50)", padding: "1px 7px", borderRadius: 10, marginRight: 4 }}>{t}</span>
              ))}
            </div>
          )}
        </div>
        {isOwn && (
          <button onClick={() => onDelete(entry.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink30)", padding: 4, flexShrink: 0 }}>
            <Icon name="trash" size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function ActivitiesTab({ date, user, activities = [], setActivities, searchQ }) {
  const [showOthers, setShowOthers] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const del = (id) => setActivities(activities.filter(a => a.id !== id));

  if (searchQ) {
    const filtered = activities.filter(a =>
      a.user === user && (
        a.title.toLowerCase().includes(searchQ) ||
        (a.notes || "").toLowerCase().includes(searchQ) ||
        (a.tags || "").toLowerCase().includes(searchQ) ||
        (a.subtype || "").toLowerCase().includes(searchQ)
      )
    );
    return (
      <div>
        <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginBottom: "0.75rem" }}>{filtered.length} result(s) in your entries</div>
        {filtered.length === 0 && <p style={{ color: "var(--ink30)", fontStyle: "italic", fontSize: "0.9rem" }}>No entries found.</p>}
        {filtered.map(e => (
          <div key={e.id}>
            <div style={{ fontSize: "0.7rem", color: "var(--ink30)", marginBottom: 2 }}>{displayDate(e.date)}</div>
            <ActivityCard entry={e} currentUser={user} onDelete={del} />
          </div>
        ))}
      </div>
    );
  }

  const dayMine = activities.filter(a => a.date === date && a.user === user);
  const dayOthers = activities.filter(a => a.date === date && a.user !== user);

  return (
    <div>
      {dayMine.length === 0 && dayOthers.length === 0 && (
        <p style={{ color: "var(--ink30)", fontStyle: "italic", fontSize: "0.9rem" }}>No entries for this day.</p>
      )}
      {dayMine.map(e => <ActivityCard key={e.id} entry={e} currentUser={user} onDelete={del} />)}
      {dayOthers.length > 0 && (
        <div style={{ marginTop: "0.75rem" }}>
          <button onClick={() => setShowOthers(v => !v)} style={{ display: "flex", alignItems: "center", gap: "0.4rem", background: "none", border: "none", cursor: "pointer", color: "var(--ink50)", fontSize: "0.78rem", padding: "0.2rem 0", marginBottom: "0.5rem" }}>
            <Icon name={showOthers ? "eyeOff" : "eye"} size={14} />
            {showOthers ? "Hide" : "Show"} others' entries ({dayOthers.length})
          </button>
          {showOthers && dayOthers.map(e => <ActivityCard key={e.id} entry={e} currentUser={user} onDelete={del} />)}
        </div>
      )}
      {showForm && (
        <Modal title="New Activity / Exercise" onClose={() => setShowForm(false)}>
          <ActivityForm date={date} user={user} onSave={e => { setActivities([...activities, e]); setShowForm(false); }} onClose={() => setShowForm(false)} />
        </Modal>
      )}
    </div>
  );
}